import { editorStoreIds, hasRole } from '../../../guards/auth/roles';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { MainHeadingSubtextComponent } from '../../atoms/main-heading-subtext/main-heading-subtext.component';
import { MainHeadingComponent } from '../../atoms/main-heading/main-heading.component';
import { FormFieldComponent } from '../../atoms/form-field/form-field.component';
import { TextAreaComponent } from '../../atoms/text-area/text-area.component';
import { FilledButtonComponent } from '../../atoms/filled-button/filled-button.component';
import { StrokedButtonComponent } from '../../atoms/stroked-button/stroked-button.component';
import { DateFieldComponent } from '../../atoms/date-field/date-field.component';
import { ImgGridComponent } from '../../molecules/img-grid/img-grid.component';
import { EmailComponent } from '../../molecules/email/email.component';
import { ReactiveFormsModule } from '@angular/forms';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import {
  SendoutDateLock,
  SendoutService,
} from '../../../services/sendout/sendout.service';
import {
  GlobalDataService,
  SessionData,
} from '../../../services/global-data/global-data.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import {
  CreateSendout,
  Options,
  Sendout,
  SendoutStatus,
  SendoutStatusEnum,
  SendoutType,
  Store,
  Template,
  UpdateSendout,
  UserType,
} from '../../../types';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TemplateService } from '../../../services/template/template.service';
import { DialogService } from '../../../services/dialog/dialog.service';
import { SelectFieldComponent } from '../../atoms/select-field/select-field.component';
import {
  MailPreviewData,
  MailPreviewService,
} from '../../../services/mail-preview/mail-preview.service';
import { ctaUrlValidator } from '../../../validation/cta-url.validator';
import { firstValueFrom, Subscription } from 'rxjs';
import { Event } from '../../../types';
import { EventsService } from '../../../services/events/events.service';
import {
  addDays,
  format,
  formatISO,
  isBefore,
  isSameDay,
  isSunday,
  parseISO,
  startOfDay,
} from 'date-fns';
import { SkeletonComponent } from '../../molecules/skeleton/skeleton.component';
import { StoreService } from '../../../services/store/store.service';
import { LinkFormFieldComponent } from '../../atoms/link-form-field/link-form-field.component';
import { RteFormFieldComponent } from '../../atoms/rte-form-field/rte-form-field.component';
import { SendoutCommentsService } from '../../../services/sendout-comments/sendout-comments.service';
import { SendoutCommentsComponent } from '../../molecules/sendout-comments/sendout-comments.component';
import { RejectionDialogComponent } from '../../molecules/rejection-dialog/rejection-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CalendarDatePickerFieldComponent } from '../../molecules/calendar-date-picker-field/calendar-date-picker-field.component';
import {
  AdvisorDraftSnapshot,
  AdvisorRecoveryDraft,
  AdvisorSessionRecoveryService,
} from '../../../services/auth/advisor-session-recovery.service';
enum SendoutState {
  New = 'NEW',
  Existing = 'EXISTING',
}

@Component({
  selector: 'app-edit-sendout',
  standalone: true,
  imports: [
    RouterLink,
    MainHeadingSubtextComponent,
    MainHeadingComponent,
    FormFieldComponent,
    FilledButtonComponent,
    StrokedButtonComponent,
    DateFieldComponent,
    ImgGridComponent,
    EmailComponent,
    ReactiveFormsModule,
    SkeletonComponent,
    LinkFormFieldComponent,
    RteFormFieldComponent,
    SendoutCommentsComponent,
    CalendarDatePickerFieldComponent,
  ],
  templateUrl: './edit-sendout.component.html',
  styleUrl: './edit-sendout.component.css',
})
export class EditSendoutComponent implements OnInit, OnDestroy {
  private sendoutLoadGeneration = 0;
  private unregisterDraftCapture?: () => void;
  private draftCaptureIdentity?: { userId: number; userType: SessionData['userType']; sessionVersion?: string; storeId: number; route: string };
  // export enums for views
  SendoutState = SendoutState;
  SendoutStatus = SendoutStatus;

  private routeSubscription?: Subscription;
  sessionDataSubscription?: Subscription;
  sessionData!: SessionData;

  sendoutState: SendoutState = SendoutState.New;
  SendoutType = SendoutType;
  sendoutForm: FormGroup;
  emailForm: FormGroup;

  sendout!: Sendout;
  template: Template | null = null;

  minSendoutDate: Date;
  isLoading: boolean = true;
  loadError: string | null = null;

  get requestedSendoutDate(): string | null {
    return this.activatedRoute.snapshot.queryParams['sendoutDate'] ?? null;
  }

  statusOptions: Options[] = [
    { value: 'afventer', viewValue: 'Afventer' },
    { value: 'godkendt', viewValue: 'Godkendt' },
    { value: 'udsendt', viewValue: 'Udsendt' },
  ];
  events: Event[] = [];
  selectedEvent: Event | null = null;

  sendoutDateLocks: SendoutDateLock[] = [];

  useEventUrlControl = new FormControl(false);

  store: Store | null = null;
  previewStoreSignature: string = 'Vejnavn 1, Bynavn';
  previewPoints: string = '1.234';

  readonly dialog = inject(MatDialog);

  constructor(
    private sendoutService: SendoutService,
    private globalDataService: GlobalDataService,
    private router: Router,
    private snackbarService: SnackbarService,
    private templateService: TemplateService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private mailPreviewService: MailPreviewService,
    private activatedRoute: ActivatedRoute,
    private eventService: EventsService,
    private storeService: StoreService,
    private sendoutCommentsService: SendoutCommentsService,
    private advisorSessionRecoveryService: AdvisorSessionRecoveryService = inject(AdvisorSessionRecoveryService)
  ) {
    this.minSendoutDate = addDays(startOfDay(new Date()), 3);
    if (isSunday(this.minSendoutDate)) {
      this.minSendoutDate = addDays(this.minSendoutDate, 1);
    }

    this.sendoutForm = new FormGroup({}); // Actual form initialization needs to happen after the template has been loaded

    this.emailForm = new FormGroup({
      email: new FormControl('', [Validators.email, Validators.required]),
    });
  }
  ngOnInit(): void {
    this.isLoading = true;
    this.sessionDataSubscription = this.globalDataService
      .getSessionData()
      .subscribe((sessionData) => {
        this.sessionData = sessionData;
        this.registerAdvisorDraftCapture(sessionData);
        this.changeDetectorRef.markForCheck();
      });
    this.routeSubscription = this.activatedRoute.url.subscribe(async (url) => {
      if (this.activatedRoute.snapshot.params['sendoutId']) {
        this.sendoutState = SendoutState.Existing;
        const sendoutId = parseInt(
          this.activatedRoute.snapshot.params['sendoutId'],
          10
        );
        await this.loadDataForSendoutId(sendoutId);
      } else {
        const generation = ++this.sendoutLoadGeneration;
        const { userId, userType, sessionVersion, store } = this.sessionData;
        const storeId = store?.id;
        const isCurrentLoad = () =>
          generation === this.sendoutLoadGeneration &&
          this.sessionData.userId === userId &&
          this.sessionData.userType === userType &&
          this.sessionData.sessionVersion === sessionVersion &&
          this.sessionData.store?.id === storeId;
        this.isLoading = true;
        this.loadError = null;
        this.selectedEvent = null;
        this.sendoutState = SendoutState.New;
        const templateId =
          this.activatedRoute.snapshot.queryParams['templateId'];
        const eventId =
          this.activatedRoute.snapshot.queryParams['eventId'] || null;

        try {
          const template = await firstValueFrom(
            this.templateService.getTemplateById(templateId)
          );
          if (!isCurrentLoad()) return;
          this.template = template;
        } catch {
          if (!isCurrentLoad()) return;
          this.loadError = 'Skabelonen kunne ikke indlæses. Vælg en skabelon igen.';
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.initializeSendoutForm();
        let sendoutDate = this.activatedRoute.snapshot.queryParams[
          'sendoutDate'
        ]
          ? parseISO(this.activatedRoute.snapshot.queryParams['sendoutDate'])
          : this.minSendoutDate;

        if (this.sessionData.store) {
          this.store = this.sessionData.store;
        }

        if (this.events.length === 0 && this.sessionData.store) {
          const events = await firstValueFrom(
            this.eventService.getAllEventsByStore(this.sessionData.store.id)
          );
          if (!isCurrentLoad()) return;
          this.events = events;
        }
        if (eventId) {
          this.selectedEvent =
            this.events.find((event) => event.id === eventId) || null;
        }

        this.sendout = this.templateService.createSendoutFromTemplate(
          sendoutDate,
          this.template,
          SendoutType.SingleStore,
          this.selectedEvent
        );

        if (this.sessionData.store) {
          this.sendout.storeIds = [this.sessionData.store.id];
        } else {
          throw new Error("Couldn't find store in session data");
        }

        this.setFormValues({ ...this.sendout });
        this.sendoutForm.enable();
        this.sendoutForm.markAllAsTouched();
        this.sendoutForm.markAsDirty();
        this.sendoutForm.updateValueAndValidity();
        this.setPreviewData();
        this.isLoading = false;
        this.registerAdvisorDraftCapture(this.sessionData);
        this.restoreAdvisorDraft();
        this.changeDetectorRef.detectChanges();
      }
    });
  }
  ngOnDestroy(): void {
    this.sendoutLoadGeneration++;
    this.sessionDataSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.unregisterDraftCapture?.();
    this.unregisterDraftCapture = undefined;
  }
  initializeSendoutForm() {
    if (!this.template) {
      throw new Error('Template is not loaded');
    }
    this.sendoutForm = new FormGroup({
      sendoutDate: new FormControl('', [Validators.required]),
      contactPerson: new FormControl('', []),
      sendoutName: new FormControl('', [Validators.required]),
      eventName: new FormControl(
        '',
        this.template.eventRequired === true ? [Validators.required] : null
      ),

      contentSubjectLine: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideSubjectLine ? false : true,
        },
        [Validators.required]
      ),
      contentPreheader: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverridePreheader ? false : true,
        },
        [Validators.required]
      ),
      contentHeadline: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideHeadline ? false : true,
        },
        [Validators.required, Validators.minLength(3)]
      ),
      contentBodyCopy: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideBodyCopy ? false : true,
        },
        [Validators.required]
      ),
      contentCtaUrl: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideCtaUrl ? false : true,
        },
        [ctaUrlValidator]
      ),
      contentImageUrl: new FormControl('', [Validators.required]),
      contentCtaText: new FormControl({
        value: '',
        disabled: this.template.allowOverrideCtaText ? false : true,
      }),
      contentDisclaimer: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideDisclaimer ? false : true,
        },
        []
      ),
    });

    this.sendoutForm.get('contentCtaUrl')?.valueChanges.subscribe(() => {
      const ctaTextControl = this.sendoutForm.get('contentCtaText');
      if (ctaTextControl?.hasError('required') && !this.hasEffectiveCtaUrl()) {
        const { required, ...otherErrors } = ctaTextControl.errors!;
        ctaTextControl.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
      }
    });

    this.sendoutForm.valueChanges.subscribe((values) => {
      this.changeDetectorRef.detectChanges();
    });

    this.sendoutForm.get('eventName')?.valueChanges.subscribe((eventName) => {
      const sendoutDateControl = this.sendoutForm.get('sendoutDate');

      if (eventName && this.selectedEvent?.type === 'Event') {
        sendoutDateControl?.setValidators([
          Validators.required,
          ...(this.selectedEvent
            ? [this.dateBeforeValidator(this.selectedEvent.startsAt)]
            : []),
        ]);
      } else if (eventName && this.selectedEvent?.type !== 'Event') {
        sendoutDateControl?.setValidators([
          Validators.required,
          ...(this.selectedEvent ? [this.dateBeforeNoEventValidator()] : []),
        ]);
      } else {
        sendoutDateControl?.clearValidators();
      }

      sendoutDateControl?.updateValueAndValidity();
    });
  }

  async loadDataForSendoutId(id: number) {
    const generation = ++this.sendoutLoadGeneration;
    const sessionData = this.sessionData;
    const sessionIdentity = {
      userId: sessionData.userId,
      userType: sessionData.userType,
      sessionVersion: sessionData.sessionVersion,
      storeId: sessionData.store?.id,
    };
    const isCurrentLoad = () =>
      generation === this.sendoutLoadGeneration &&
      this.sessionData.userId === sessionIdentity.userId &&
      this.sessionData.userType === sessionIdentity.userType &&
      this.sessionData.sessionVersion === sessionIdentity.sessionVersion &&
      this.sessionData.store?.id === sessionIdentity.storeId;
    this.isLoading = true;
    this.loadError = null;
    this.changeDetectorRef.detectChanges();
    try {
      // Load template, sendout and event data
      const sendout = await firstValueFrom(this.sendoutService.getSendoutById(id));
      if (!isCurrentLoad()) return;
      this.sendout = sendout;
      this.selectedEvent = null;
      if (!sendout) {
        throw new Error(`Sendout with id ${id} not found`);
      }
      const allowedStores = this.canManageSendouts
        ? sendout.storeIds
        : sendout.storeIds.filter(storeId => editorStoreIds(sessionData).includes(storeId));
      const selectedStore = sessionData.store?.id;
      const previewStoreId = (!this.canManageSendouts || sessionData.storeImpersonation) &&
        selectedStore !== undefined && allowedStores.includes(selectedStore)
        ? selectedStore : allowedStores[0];
      if (previewStoreId === undefined) throw new Error('No authorized store for this sendout');

      const template = await firstValueFrom(
        this.templateService.getTemplateById(sendout.sendoutTemplateId, sendout.id)
      );
      if (!isCurrentLoad()) return;
      this.template = template;
      if (sendout.eventId) {
        const events = await firstValueFrom(
          this.eventService.getAllEventsByStore(previewStoreId)
        );
        if (!isCurrentLoad()) return;
        this.events = events;
      }
      if (sendout.eventId) {
        this.selectedEvent =
          this.events.find((e) => e.id === sendout.eventId) || null;
      }

      const store = await firstValueFrom(
        this.storeService.getStoreById(previewStoreId)
      );
      if (!isCurrentLoad()) return;
      this.store = store;

      this.initializeSendoutForm();
      this.setFormValues({ ...sendout });
      this.sendoutForm.markAsPristine();
      this.sendoutForm.enable();

      if (
        sendout.status !== SendoutStatus.Draft ||
        this.isStorePreview
      ) {
        this.sendoutForm.disable({ emitEvent: false });
      }

      this.setPreviewData();
    } catch (error) {
      if (!isCurrentLoad()) return;
      console.error('Could not load sendout:', error);
      this.loadError = 'Udsendelsen kunne ikke indlæses. Du har muligvis ikke adgang til den.';
      this.snackbarService.show(this.loadError);
    } finally {
      if (isCurrentLoad()) {
        this.isLoading = false;
        if (!this.loadError && this.sendout && this.template && this.sendoutForm) {
          this.registerAdvisorDraftCapture(this.sessionData);
        }
        this.restoreAdvisorDraft();
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  private registerAdvisorDraftCapture(sessionData: SessionData): void {
    const storeId = sessionData.store?.id;
    const route = this.router.url;
    const identity = { userId: sessionData.userId, userType: sessionData.userType, sessionVersion: sessionData.sessionVersion, storeId: storeId ?? 0, route };
    if (this.draftCaptureIdentity &&
      this.draftCaptureIdentity.userId === identity.userId &&
      this.draftCaptureIdentity.userType === identity.userType &&
      this.draftCaptureIdentity.sessionVersion === identity.sessionVersion &&
      this.draftCaptureIdentity.storeId === identity.storeId &&
      this.draftCaptureIdentity.route === identity.route) return;
    this.unregisterDraftCapture?.();
    this.unregisterDraftCapture = undefined;
    this.draftCaptureIdentity = undefined;
    if (!sessionData.initialized || sessionData.userType !== UserType.AgillicAdvisor || storeId === undefined) return;

    this.draftCaptureIdentity = identity;
    this.unregisterDraftCapture = this.advisorSessionRecoveryService.registerDraftCapture(() => {
      if (!this.isCaptureContextCurrent(identity) || this.isLoading || !this.sendoutForm || this.sendoutForm.pristine ||
        !this.sendout || !this.template || this.sendout.sendoutType !== SendoutType.SingleStore ||
        !this.sendout.storeIds.includes(identity.storeId) ||
        (this.sendoutState === SendoutState.Existing && this.sendout.status !== SendoutStatus.Draft)) return null;

      const keys = this.editableDraftFieldKeys;
      const rawValues = this.sendoutForm.getRawValue();
      const fields: Record<string, unknown> = {};
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(rawValues, key)) fields[key] = rawValues[key];
      }
      return {
        kind: 'sendout',
        fields,
        sendoutId: this.sendoutState === SendoutState.Existing ? this.sendout.id : null,
        templateId: this.template.id,
        eventId: this.selectedEvent?.id ?? this.sendout.eventId ?? null,
      } satisfies AdvisorDraftSnapshot;
    });
  }

  private get editableDraftFieldKeys(): string[] {
    return ['sendoutDate', 'sendoutName', 'contactPerson', 'contentSubjectLine', 'contentPreheader',
      'contentHeadline', 'contentBodyCopy', 'contentCtaText', 'contentCtaUrl', 'contentImageUrl',
      'contentDisclaimer', 'eventName'];
  }

  private isCaptureContextCurrent(identity: { userId: number; userType: SessionData['userType']; sessionVersion?: string; storeId: number; route: string }): boolean {
    return this.router.url === identity.route && this.sessionData?.initialized === true &&
      this.sessionData.userId === identity.userId && this.sessionData.userType === identity.userType &&
      this.sessionData.sessionVersion === identity.sessionVersion &&
      this.sessionData.store?.id === identity.storeId;
  }

  private restoreAdvisorDraft(): void {
    if (this.isLoading || !this.sendoutForm || !this.sendout || !this.template || !this.sessionData?.initialized ||
      this.sessionData.userType !== UserType.AgillicAdvisor || this.sendout.sendoutType !== SendoutType.SingleStore ||
      !this.sessionData.store || !this.sendout.storeIds.includes(this.sessionData.store.id) ||
      (this.sendoutState === SendoutState.Existing && this.sendout.status !== SendoutStatus.Draft)) return;

    const draft = this.advisorSessionRecoveryService.takeDraftForCurrentRoute(
      (draft) => this.draftMatchesCurrentContext(draft)
    );
    if (!draft || !this.draftMatchesCurrentContext(draft)) return;
    if (!window.confirm('Der blev fundet en ikke-gemt kladde. Vil du gendanne dine ændringer?')) return;

    const fields: Record<string, unknown> = {};
    for (const key of this.editableDraftFieldKeys) {
      if (Object.prototype.hasOwnProperty.call(draft.fields, key)) fields[key] = draft.fields[key];
    }
    if (Object.prototype.hasOwnProperty.call(fields, 'sendoutDate')) {
      const dateValue = fields['sendoutDate'];
      const parsedDate = dateValue instanceof Date ? dateValue : parseISO(String(dateValue));
      if (!Number.isNaN(parsedDate.getTime())) fields['sendoutDate'] = parsedDate;
      else delete fields['sendoutDate'];
    }
    this.sendoutForm.patchValue(fields);
    this.sendoutForm.markAsDirty();
    this.sendoutForm.updateValueAndValidity();
    this.setPreviewData();
    this.changeDetectorRef.detectChanges();
  }

  private draftMatchesCurrentContext(draft: AdvisorRecoveryDraft): boolean {
    const params = this.activatedRoute.snapshot.queryParams;
    return draft.storeId === this.sessionData.store?.id &&
      draft.sendoutId === (this.sendoutState === SendoutState.Existing ? this.sendout.id : null) &&
      draft.templateId === this.template?.id && draft.eventId === (this.selectedEvent?.id ?? this.sendout.eventId ?? null) &&
      (this.sendoutState !== SendoutState.New || Number(params['templateId']) === this.template?.id);
  }

  get canManageSendouts(): boolean {
    return !!this.sessionData && hasRole(this.sessionData, 'SendoutManager');
  }

  get storeWorkflow(): boolean {
    return !this.canManageSendouts || this.sessionData.storeImpersonation;
  }

  get canViewSendout(): boolean {
    return this.canManageSendouts || (!!this.sessionData && !!this.sendout &&
      (this.store
        ? editorStoreIds(this.sessionData).includes(this.store.id) && this.sendout.storeIds.includes(this.store.id)
        : this.sendout.storeIds.some(id => editorStoreIds(this.sessionData).includes(id))));
  }

  get isStorePreview(): boolean {
    return !this.canManageSendouts && this.sendout?.sendoutType === SendoutType.MultiStore;
  }

  get canChangeSendout(): boolean {
    return this.canViewSendout && !this.isStorePreview &&
      this.sendout.status !== SendoutStatus.Executing && this.sendout.status !== SendoutStatus.Completed;
  }

  setPreviewData() {
    const intFormat = new Intl.NumberFormat('da-DK', {
      style: 'decimal',
      maximumFractionDigits: 0,
    });
    this.previewPoints = intFormat.format(parseInt(format(new Date(), 'Hmm')));

    if (this.store) {
      // const [city, address] = this.store.place.split(' - ', 2);
      this.previewStoreSignature = this.store.signature;
    }
  }

  dateBeforeValidator(referenceDate: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // If the control is empty, let other validators handle it (like required validator)
      }

      const inputDate = new Date(control.value);

      // Allow dates before the startDate
      if (
        isSameDay(inputDate, referenceDate) ||
        isBefore(inputDate, referenceDate)
      ) {
        return null; // Date is valid
      }
      return { dateBefore: true }; // Date is not valid
    };
  }
  dateBeforeNoEventValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const inputDate = new Date(control.value);
      const currDate = startOfDay(new Date());

      if (inputDate > currDate) {
        return null;
      }
      return { dateBefore: true };
    };
  }

  /**
   *
   * @param newStatus
   * @param forceSave  - if true, the form will be saved even if we're not saving a draft
   * @returns
   */
  onSendoutFormSubmit(
    newStatus: SendoutStatusEnum,
    forceSave: boolean = false,
    confirmedWithoutCta: boolean = false
  ) {
    if (!this.canChangeSendout || (!this.canManageSendouts &&
        newStatus !== SendoutStatus.Draft && newStatus !== SendoutStatus.AwaitingApproval)) return;
    // Check form validity - invalid forms may be saved to draft
    if (this.sendoutForm.invalid && newStatus !== SendoutStatus.Draft) {
      this.sendoutForm.markAllAsTouched();
      this.sendoutForm.updateValueAndValidity();
      return;
    }

    // An unresolved existing reference may still resolve during export.
    // Require an explicit link choice instead of promising an email without a button.
    if (
      newStatus === SendoutStatus.AwaitingApproval &&
      this.sendoutForm.get('contentCtaUrl')?.value === '{{event URL}}' &&
      this.sendout?.eventId &&
      !this.selectedEvent
    ) {
      this.dialogService.openDialog(
        'Eventlink kan ikke bekræftes',
        'Det tilknyttede event er ikke tilgængeligt i listen. Det gemte eventlink er bevaret. Vælg et event igen, indsæt et link manuelt, eller fravælg eventlinket, før du sender til godkendelse.',
        'Tilbage',
        () => {}
      );
      return;
    }

    if (
      newStatus === SendoutStatus.AwaitingApproval &&
      this.hasEffectiveCtaUrl() &&
      !this.hasCtaText()
    ) {
      const ctaTextControl = this.sendoutForm.get('contentCtaText');
      ctaTextControl?.setErrors({ required: true });
      ctaTextControl?.markAsTouched();
      return;
    }

    if (
      newStatus === SendoutStatus.AwaitingApproval &&
      !confirmedWithoutCta &&
      this.hasCtaTextWithoutUrl()
    ) {
      this.dialogService.openDialog(
        'Fortsæt uden knap',
        'Events fra bookingsystemet kræver et link. Er du sikker på, at du ikke vil indsætte et link?',
        'Fortsæt uden knap',
        () => this.onSendoutFormSubmit(newStatus, forceSave, true),
        undefined,
        '500px',
        undefined,
        undefined,
        undefined,
        undefined,
        'Tilbage'
      );
      return;
    }

    if (this.sendoutState === SendoutState.New) {
      const reviewMetadata = this.getReviewMetadata(newStatus, true);
      const payload: CreateSendout = {
        sendoutType: this.sendout?.sendoutType,
        sendoutTemplateId: this.sendout?.sendoutTemplateId,
        storeIds: this.sendout?.storeIds,
        createdBy: this.sessionData.userId,
        updatedBy: this.sessionData.userId,
        // updatedAt: new Date(),
        sendoutName:
          this.sendoutForm.value.sendoutName === undefined
            ? this.sendout.sendoutName
            : this.sendoutForm.value.sendoutName,
        status: newStatus,
        eventId: this.sendout?.eventId || null,
        reviewedBy: reviewMetadata.reviewedBy,
        reviewedAt: reviewMetadata.reviewedAt,

        contentSubjectLine:
          this.sendoutForm.value.contentSubjectLine === undefined
            ? this.sendout.contentSubjectLine
            : this.sendoutForm.value.contentSubjectLine,
        contentPreheader: this.sendout?.contentPreheader,
        contentHeadline:
          this.sendoutForm.value.contentHeadline === undefined
            ? this.sendout.contentHeadline
            : this.sendoutForm.value.contentHeadline,
        contentBodyCopy:
          this.sendoutForm.value.contentBodyCopy === undefined
            ? this.sendout.contentBodyCopy
            : this.sendoutForm.value.contentBodyCopy,
        contentCtaText:
          this.sendoutForm.value.contentCtaText === undefined
            ? this.sendout.contentCtaText
            : this.sendoutForm.value.contentCtaText,
        contentCtaUrl:
          this.sendoutForm.value.contentCtaUrl === undefined
            ? this.sendout.contentCtaUrl
            : this.sendoutForm.value.contentCtaUrl,
        contentImageUrl:
          this.sendoutForm.value.contentImageUrl === undefined
            ? this.sendout.contentImageUrl
            : this.sendoutForm.value.contentImageUrl,
        contentDisclaimer:
          this.sendoutForm.value.contentDisclaimer === undefined
            ? this.sendout.contentDisclaimer
            : this.sendoutForm.value.contentDisclaimer,
        contactPerson:
          (this.sendoutForm.value.contactPerson === undefined
            ? this.sendout.contactPerson
            : this.sendoutForm.value.contactPerson) || '',
        sendoutDate: formatISO(new Date(this.sendoutForm.value.sendoutDate), {
          representation: 'date',
        }),
      };
      return this.executeCreateSendout(payload);
    }

    if (newStatus === SendoutStatus.Draft || forceSave) {
      const reviewMetadata = this.getReviewMetadata(
        newStatus,
        this.sendout?.status !== newStatus
      );
      const payload: UpdateSendout = {
        sendoutType: this.sendout?.sendoutType,
        sendoutTemplateId: this.sendout?.sendoutTemplateId,
        storeIds: this.sendout?.storeIds,
        updatedBy: this.sessionData.userId,
        // updatedAt: new Date(),
        sendoutName:
          this.sendoutForm.value.sendoutName === undefined
            ? this.sendout.sendoutName
            : this.sendoutForm.value.sendoutName,
        status: newStatus,
        eventId: this.sendout?.eventId,
        reviewedBy: reviewMetadata.reviewedBy,
        reviewedAt: reviewMetadata.reviewedAt,
        contentSubjectLine:
          this.sendoutForm.value.contentSubjectLine === undefined
            ? this.sendout.contentSubjectLine
            : this.sendoutForm.value.contentSubjectLine,
        contentPreheader: this.sendout?.contentPreheader,
        contentHeadline:
          this.sendoutForm.value.contentHeadline === undefined
            ? this.sendout.contentHeadline
            : this.sendoutForm.value.contentHeadline,
        contentBodyCopy:
          this.sendoutForm.value.contentBodyCopy === undefined
            ? this.sendout.contentBodyCopy
            : this.sendoutForm.value.contentBodyCopy,
        contentCtaText:
          this.sendoutForm.value.contentCtaText === undefined
            ? this.sendout.contentCtaText
            : this.sendoutForm.value.contentCtaText,
        contentCtaUrl:
          this.sendoutForm.value.contentCtaUrl === undefined
            ? this.sendout.contentCtaUrl
            : this.sendoutForm.value.contentCtaUrl,
        contentImageUrl:
          this.sendoutForm.value.contentImageUrl === undefined
            ? this.sendout.contentImageUrl
            : this.sendoutForm.value.contentImageUrl,
        contentDisclaimer:
          this.sendoutForm.value.contentDisclaimer === undefined
            ? this.sendout.contentDisclaimer
            : this.sendoutForm.value.contentDisclaimer,
        contactPerson:
          (this.sendoutForm.value.contactPerson === undefined
            ? this.sendout.contactPerson
            : this.sendoutForm.value.contactPerson) || '',
        sendoutDate: formatISO(new Date(this.sendoutForm.value.sendoutDate), {
          representation: 'date',
        }),
        id: this.sendout?.id,
      };
      return this.executeUpdateSendout(payload);
    } else {
      return this.updateSendoutStatus(newStatus);
    }
  }

  private hasEffectiveCtaUrl(): boolean {
    const ctaUrl = (this.sendoutForm.get('contentCtaUrl')?.value || '')
      .toString()
      .trim();

    return (
      ctaUrl !== '' &&
      (ctaUrl !== '{{event URL}}' || !!this.selectedEvent?.link?.trim())
    );
  }

  private hasCtaTextWithoutUrl(): boolean {
    return this.hasCtaText() && !this.hasEffectiveCtaUrl();
  }

  private hasCtaText(): boolean {
    return !!this.sendoutForm
      .get('contentCtaText')
      ?.value?.toString()
      .trim();
  }

  async sendProofEmail() {
    if (
      this.canViewSendout &&
      this.emailForm.valid &&
      (this.sendoutForm.valid || this.sendoutForm.status == 'DISABLED') &&
      this.sendout &&
      this.template
    ) {
      // Assemble preview data
      const data: MailPreviewData = {
        templateName: this.template.templateName,
        data: {
          CONTENT_BODY_COPY: this.sendoutForm.get('contentBodyCopy')?.value,
          CONTENT_CTA_TEXT: this.sendoutForm.get('contentCtaText')?.value,
          CONTENT_CTA_URL:
            this.sendoutForm.get('contentCtaUrl')?.value == '{{event URL}}'
              ? this.selectedEvent != null
                ? this.selectedEvent.link
                : ''
              : this.sendoutForm.get('contentCtaUrl')?.value,
          CONTENT_DISCLAIMER: this.sendoutForm.get('contentDisclaimer')?.value,
          CONTENT_HEADLINE: this.sendoutForm.get('contentHeadline')?.value,
          CONTENT_IMAGE_URL: this.sendoutForm.get('contentImageUrl')?.value,
          CONTENT_SUBJECT_LINE:
            this.sendoutForm.get('contentSubjectLine')?.value,
          CONTENT_PREHEADER: this.sendoutForm.get('contentPreheader')?.value,
          POINTS: this.previewPoints,
          CONTENT_STORE_SIGNATURE: this.previewStoreSignature,
        },
      };
      this.emailForm.disable();
      this.mailPreviewService
        .sendMailPreview(this.emailForm.value.email, data)
        .then(() => {
          this.emailForm.enable();
          this.snackbarService.show('Emailen er afsendt', 1200);
        })
        .catch(() => {
          this.emailForm.enable();
          this.snackbarService.show('Emailen blev ikke afsendt', 5000);
        });
    } else if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      this.emailForm.updateValueAndValidity();
    }
  }

  setFormValues(sendout: Sendout): void {
    this.sendoutForm.patchValue({
      sendoutDate: sendout.sendoutDate,
      sendoutName: sendout.sendoutName,
      contactPerson:
        sendout.contactPerson !== null ? sendout.contactPerson : '',
      contentImageUrl: sendout.contentImageUrl,
      eventName: this.selectedEvent ? this.selectedEvent.name : '',
      contentSubjectLine: sendout.contentSubjectLine,
      contentPreheader: sendout.contentPreheader,
      contentHeadline: sendout.contentHeadline,
      contentBodyCopy: sendout.contentBodyCopy,
      contentCtaUrl: sendout.contentCtaUrl,
      contentCtaText: sendout.contentCtaText,
      contentDisclaimer: sendout.contentDisclaimer,
    });
  }

  async executeCreateSendout(payload: CreateSendout): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.sendoutService.createSendout(payload)
      );
      this.snackbarService.show('Ændringerne er gemt');
      this.router.navigate(['sendout', res.id]);
    } catch (error) {
      console.error('error :', error);
      this.snackbarService.show('Ændringerne kunne ikke gemmes');
      this.changeDetectorRef.detectChanges();
    }
  }

  async executeUpdateSendout(payload: UpdateSendout): Promise<void> {
    try {
      await firstValueFrom(this.sendoutService.updateSendout(payload));
      this.snackbarService.show('Ændringerne er gemt');
      await this.loadDataForSendoutId(payload.id);
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      console.error('error :', error);
      this.snackbarService.show('Ændringerne kunne ikke gemmes');
      this.changeDetectorRef.detectChanges();
    }
  }

  private getReviewMetadata(
    status: SendoutStatusEnum,
    isReviewAction: boolean
  ): Pick<UpdateSendout, 'reviewedBy' | 'reviewedAt'> {
    if (
      isReviewAction &&
      (status === SendoutStatus.Approved || status === SendoutStatus.Rejected)
    ) {
      return {
        reviewedBy: this.sessionData.userId,
        reviewedAt: formatISO(new Date()),
      };
    }

    return {
      reviewedBy: this.sendout?.reviewedBy || null,
      reviewedAt: this.sendout?.reviewedAt
        ? formatISO(this.sendout.reviewedAt)
        : null,
    };
  }

  /**
   * Update the sendout status and possibly reviewedBy/reviewedAt fields
   * @param status
   * @returns
   */
  async updateSendoutStatus(status: SendoutStatusEnum): Promise<void> {
    if (!this.canChangeSendout || (!this.canManageSendouts &&
        status !== SendoutStatus.Draft && status !== SendoutStatus.AwaitingApproval)) return;
    const reviewMetadata = this.getReviewMetadata(status, true);
    const payload: UpdateSendout = {
      sendoutType: this.sendout.sendoutType,
      sendoutTemplateId: this.sendout.sendoutTemplateId,
      storeIds: this.sendout.storeIds,
      updatedBy: this.sendout.updatedBy,
      reviewedBy: reviewMetadata.reviewedBy,
      reviewedAt: reviewMetadata.reviewedAt,
      sendoutName: this.sendout.sendoutName,
      status: status,
      eventId: this.sendout.eventId,
      contentSubjectLine: this.sendout.contentSubjectLine,
      contentPreheader: this.sendout.contentPreheader,
      contentHeadline: this.sendout.contentHeadline,
      contentBodyCopy: this.sendout.contentBodyCopy,
      contentCtaText: this.sendout.contentCtaText,
      contentCtaUrl: this.sendout.contentCtaUrl,
      contentImageUrl: this.sendout.contentImageUrl,
      contentDisclaimer: this.sendout.contentDisclaimer,
      contactPerson: this.sendout.contactPerson || '',
      sendoutDate: formatISO(this.sendout.sendoutDate, {
        representation: 'date',
      }),
      id: this.sendout.id,
    };
    return this.executeUpdateSendout(payload);
  }

  goToSelectEventPage() {
    const nav = () => {
      this.router.navigate(['sendout', 'create', 'select-event'], {
        queryParams: {
          templateId: this.template?.id,
          eventId: this.selectedEvent?.id,
          sendoutDate: format(this.sendout.sendoutDate, 'yyyy-MM-dd'),
        },
      });
    };

    if (this.sendoutState == SendoutState.New) {
      this.confirmEventNavigation(nav);
    }
  }

  private confirmEventNavigation(navigate: () => void) {
    if (this.sendoutForm.dirty) {
      this.dialogService.openDialog(
        'Skift event',
        'Du har ændringer, som ikke er gemt. Hvis du fortsætter, bliver ændringerne kasseret.',
        'Fortsæt uden at gemme',
        navigate,
        undefined,
        '500px',
        undefined,
        undefined,
        undefined,
        undefined,
        'Bliv på siden'
      );
    } else {
      navigate();
    }
  }

  afbrydClick = () => {
    this.router.navigate(['landing']);
  };

  sletUdsendelseClick() {
    if (!this.canChangeSendout) return;
    this.dialogService.openDialog(
      'Slet udsendelse',
      'Er du sikker på, at du vil slette denne udsendelse?',
      'Slet',
      async () => {
        await this.deleteSendoutById(this.sendout.id).then(() => {
          this.router.navigate(['sendouts']);
        });
      },
      undefined,
      '347px',
      'content-container',
      '',
      '',
      'dialog-actions'
    );
  }

  editEventIconClick = () => {
    if (!this.canChangeSendout || this.sendout.status !== SendoutStatus.Draft) return;
    if (this.sendoutState === SendoutState.New) {
      this.goToSelectEventPage();
      return;
    }
    this.confirmEventNavigation(() => {
      this.router.navigate(['sendout', this.sendout.id, 'select-event']);
    });
  };

  deleteSendoutById = (id: number) => {
    return new Promise((resolve, reject) => {
      this.sendoutService.deleteSendoutById(id).subscribe({
        next: (data) => {
          this.snackbarService.show('Udsendelsen er slettet');
          this.changeDetectorRef.detectChanges();
          resolve(data);
        },
        error: (error) => {
          console.error('error :', error);
          this.changeDetectorRef.detectChanges();
          reject(error);
        },
        complete: () => {
          this.changeDetectorRef.detectChanges();
        },
      });
    });
  };

  rejectionClick(forceSave: boolean = false) {
    if (!this.canManageSendouts || !this.canChangeSendout) return;
    const dialogRef = this.dialog.open(RejectionDialogComponent, {
      width: '500px',
      maxWidth: '100vw',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && typeof result.comment != 'undefined') {
        await this.onSendoutFormSubmit(SendoutStatus.Rejected, forceSave);

        const comment = result.comment.toString().trim();
        await firstValueFrom(
          this.sendoutCommentsService.addCommentForSendout(
            this.sendout.id,
            comment,
            undefined,
            undefined,
            { action: 'reject' }
          )
        );
      }
    });
  }
}
